class ScrollVideo extends HTMLElement {
  constructor() {
    super();
    this.video = this.querySelector('video');
    this.videoDuration = 0;
    this.scrollTrigger = null;
    this.initialized = false;
    this.isDestroying = false;
    this.lastAnnotationUpdate = 0;
  }

  connectedCallback() {
    if (!this.video) {
      console.error('No video element found');
      return;
    }

    // Prevent re-initialization
    if (this.initialized) return;

    // Get annotations
    this.annotations = Array.from(this.querySelectorAll('.scroll-video__annotation'));

    // Wait for GSAP and ScrollTrigger to load
    this.initWhenReady();
  }

  initWhenReady() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      setTimeout(() => this.initWhenReady(), 100);
      return;
    }

    this.init();
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    gsap.registerPlugin(ScrollTrigger);

    // Force load the video
    this.video.load();

    // Ensure video is ready and seekable
    const setupScrollTrigger = () => {
      this.videoDuration = this.video.duration;
      if (this.videoDuration && this.videoDuration > 0) {
        this.setupGSAPScrollTrigger();
      }
    };

    // Wait for video to be seekable (readyState >= 2 means HAVE_CURRENT_DATA)
    // Use { once: true } to prevent repeated event firing
    this.video.addEventListener('loadeddata', setupScrollTrigger, { once: true });
    this.video.addEventListener('canplay', setupScrollTrigger, { once: true });

    // Fallback if already loaded
    if (this.video.readyState >= 2 && this.video.duration) {
      setupScrollTrigger();
    }
  }

  setupGSAPScrollTrigger() {
    // Prevent setup if already destroying or if already has a trigger
    if (this.isDestroying || this.scrollTrigger) return;

    const container = this.closest('.scroll-video');
    if (!container) {
      console.error('Could not find scroll-video container');
      return;
    }

    // Store the target time for smoother updates
    let targetTime = 0;
    let rafId = null;

    // Smooth update function using RAF
    const updateVideoTime = () => {
      if (!this.isDestroying && this.video && Math.abs(this.video.currentTime - targetTime) > 0.001) {
        this.video.currentTime = targetTime;
      }
    };

    // Create a ScrollTrigger that pins the container and directly updates video time
    this.scrollTrigger = ScrollTrigger.create({
      trigger: container,
      start: 'top top',
      end: '+=350%', // Longer scroll for video content
      pin: true,
      pinSpacing: true,
      anticipatePin: 1,
      scrub: 10,
      onUpdate: (self) => {
        if (!this.isDestroying && this.video) {
          // Video completes at 95% scroll, last 5% is quick exit
          const videoProgress = Math.min(self.progress / 0.95, 1);
          targetTime = videoProgress * this.videoDuration;
          // Use RAF for smoother updates
          if (!rafId) {
            rafId = requestAnimationFrame(() => {
              updateVideoTime();
              rafId = null;
            });
          }

          // Update annotations based on video progress (throttled to every 50ms)
          const now = Date.now();
          if (now - this.lastAnnotationUpdate > 50) {
            this.updateAnnotations(videoProgress * 100);
            this.lastAnnotationUpdate = now;
          }
        }
      },
      onRefresh: (self) => {
        // Remove any padding that ScrollTrigger adds
        if (container.style.padding) {
          container.style.padding = '';
        }
      },
      markers: false
    });

    // Reset destroying flag after ScrollTrigger is created
    this.isDestroying = false;
  }

  updateAnnotations(progress) {
    if (!this.annotations || this.annotations.length === 0) return;

    this.annotations.forEach(annotation => {
      const startTime = parseFloat(annotation.dataset.startTime) || 0;
      const endTime = parseFloat(annotation.dataset.endTime) || 100;
      const wasActive = annotation.classList.contains('active');

      // Show annotation if current progress is within its time range
      if (progress >= startTime && progress <= endTime) {
        annotation.classList.remove('leaving');
        annotation.classList.add('active');
      } else {
        // If it was active and now isn't, add leaving class for mobile slide-out
        if (wasActive && progress > endTime) {
          annotation.classList.add('leaving');
          // Remove leaving class after animation completes
          setTimeout(() => {
            annotation.classList.remove('leaving');
          }, 400);
        }
        annotation.classList.remove('active');
      }
    });
  }

  disconnectedCallback() {
    // Don't clean up if we have an active ScrollTrigger (it's just pinning/unpinning)
    if (this.scrollTrigger) return;

    // Prevent recursive calls
    if (this.isDestroying) return;

    this.isDestroying = true;

    // Clean up when element is removed
    if (this.scrollTrigger) {
      try {
        this.scrollTrigger.kill(true);
        this.scrollTrigger = null;
      } catch (e) {
        console.warn('Error killing ScrollTrigger:', e);
      }
    }

    this.initialized = false;
  }
}

// Register custom element - wait for GSAP to be available
if (typeof gsap !== 'undefined') {
  if (!customElements.get('scroll-video')) {
    customElements.define('scroll-video', ScrollVideo);
  }
} else {
  // Retry when DOM is ready or GSAP loads
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof gsap !== 'undefined' && !customElements.get('scroll-video')) {
      customElements.define('scroll-video', ScrollVideo);
    }
  });
}
